import { zodResolver } from '@hookform/resolvers/zod';
import { Save, Delete as Trash2 } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import { FieldErrors, FormProvider, Path, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { BasicInfoSection } from '@/components/codex-form/basic-info';
import { CustomFieldsSection } from '@/components/codex-form/custom-fields-section';
import { ImageManager } from '@/components/codex-form/image-manager';
import { FloatingSaveButton } from '@/components/floating-save-button';
import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { Page } from '@/components/page';
import {
  CodexFormSchema,
  CodexFormValues,
  useCodexAccessLayer,
} from '@/features/codex';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { Codex as DomainCodex } from '@/lib/types';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

export const Route = createFileRoute('/codex/edit/$id')({
  component: EditCodexEntryPage,
});

function EditCodexEntryForm() {
  const { id: entryId } = Route.useParams();
  const { novel } = useNovelRepository();
  const novelId = novel?.id;
  const resolveResponsive = useResponsiveValue();

  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const methods = useForm<CodexFormValues>({
    resolver: zodResolver(CodexFormSchema),
    defaultValues: {
      name: '',
      type: '',
      shortDescription: '',
      content: '',
      visualDescription: '',
      images: [],
      primaryImageId: null,
      pendingFiles: [],
    },
  });

  const {
    entry: combinedData,
    sections: codexSettings,
    isLoading: isLoadingLayer,
    updateEntry,
    deleteEntry,
  } = useCodexAccessLayer(entryId, methods.watch('type'));

  const { processImages } = useImageProcessor();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [lastResetId, setLastResetId] = useState<string | null>(null);

  // Sync images and primary index from background updates (e.g. AI generation)
  useEffect(() => {
    if (combinedData?.images && JSON.stringify(combinedData.images) !== JSON.stringify(methods.getValues('images'))) {
      methods.setValue('images', combinedData.images as CodexFormValues['images'], { shouldDirty: false });
    }
    if (combinedData?.primaryImageId !== undefined && combinedData.primaryImageId !== methods.getValues('primaryImageId')) {
      methods.setValue('primaryImageId', combinedData.primaryImageId, { shouldDirty: false });
    }
  }, [combinedData?.images, combinedData?.primaryImageId, methods]);

  // Load existing data into form
  useEffect(() => {
    if (combinedData && !isLoadingLayer && lastResetId !== entryId) {
      methods.reset(combinedData as CodexFormValues);
      setLastResetId(entryId);
      setIsLoading(false);
    }
  }, [combinedData, isLoadingLayer, entryId, methods, lastResetId]);

  const onSubmit = async (values: CodexFormValues) => {
    if (!novelId || !entryId || !combinedData || !novel?.ownerId) return;

    const { pendingFiles, ...entryData } = values;
    const { defined: coreData, details } = splitBySchemaShape(CodexFormSchema, entryData);

    setIsSaving(true);
    try {
      let imagesUpdatedArray = [...(coreData.images || [])];

      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `codex/${entryId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || '';
      const now = new Date().toISOString();

      await updateEntry(entryId, {
        ...coreData,
        id: entryId,
        details,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
        audit: {
          createdAt: combinedData.audit.createdAt,
          updatedAt: now,
        },
      });

      toast({
        title: t('common.success'),
        description: t('entity_form.codex.update_success_desc', { name: coreData.name }),
      });

      navigate({ to: '/codex' });
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: t('entity_form.common.save_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteEntry(entryId);
      toast({
        title: t('common.deleted'),
        description: t('entity_form.codex.delete_success_desc'),
      });
      navigate({ to: '/codex' });
    } finally {
      setIsDeleting(false);
    }
  };

  const onInvalid = (errors: FieldErrors<CodexFormValues>) => {
    const errorFields = Object.keys(errors).map(key => {
      return key.charAt(0).toUpperCase() + key.slice(1);
    });

    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_desc', { fields: errorFields.join(', ') }),
      variant: 'destructive',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
        <CircularProgress color="primary" thickness={2} size={48} />
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.2em" }}>
          {t('entity_form.common.initializing')}
        </Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit, onInvalid)}>
        <Page
          title={methods.watch('name') ? t('entity_form.codex.edit_dynamic_title', { name: methods.watch('name') }) : t('entity_form.codex.edit_title')}
          description={t('entity_list.codex.description')}
          breadcrumbs={[
            { label: t('entity_list.codex.title'), href: '/codex' },
            { label: (methods.watch('name') as string) || t('entity_view.codex.view_fallback'), href: `/codex/view/${entryId}` },
            { label: t('entity_form.common.edit') }
          ]}
          layoutMode="scrolling"
          actions={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="error"
                disabled={isSaving || isDeleting}
                onClick={() => setShowDeleteConfirm(true)}
                startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Trash2 sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2 }}
              >
                {t('entity_form.common.delete')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSaving || isDeleting}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {t('common.save')}
              </Button>
            </Stack>
          }
        >

          <DeleteConfirmationModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            isDeleting={isDeleting}
            itemName={combinedData?.name}
            description={t('entity_form.codex.delete_desc')}
          />

          <Grid container spacing={4}>
            <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
              <Stack spacing={4}>
                <BasicInfoSection isEdit={true} />
                {novelId && <CustomFieldsSection novelId={novelId} sections={codexSettings} />}
              </Stack>
            </Grid>

            <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
              <Box component="aside" sx={{ position: { lg: "sticky" }, top: 24 }}>
                {novelId && (
                  <ImageManager
                    onUpdate={(updates) => {
                      (Object.keys(updates) as Array<keyof typeof updates>).forEach((key) => {
                        const value = updates[key];
                        if (value !== undefined) {
                          methods.setValue(key as Path<CodexFormValues>, value, { shouldDirty: true, shouldTouch: true });
                        }
                      });
                    }}
                    entry={{
                      ...methods.watch(),
                      id: entryId
                    } as DomainCodex}
                    pendingFiles={(methods.watch('pendingFiles') as File[]) || []}
                    visualDescription={methods.watch('visualDescription')}
                    onVisualDescriptionChange={(value) => methods.setValue('visualDescription', value, { shouldDirty: true })}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isDeleting}
          label={t('common.save')}
        />
      </form>
    </FormProvider>
  );
}

function EditCodexEntryPage() {
  const { t } = useTranslation();
  return (
    <FeatureGuard featureKey="should_display_codex_page">
      <AuthGuard>
        <Suspense fallback={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 3 }}>
            <CircularProgress color="primary" thickness={2} size={48} />
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.2em" }}>
              {t('entity_form.common.initializing')}
            </Typography>
          </Box>
        }>
          <EditCodexEntryForm />
        </Suspense>
      </AuthGuard>
    </FeatureGuard>
  );
}
